import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Mail, Eye, Send } from "lucide-react";
import { DigestCcSettings, DealerCcEditor } from "./DigestRecipients";
import { DigestSchedule, DealerTimezone, zoneName } from "./DigestSchedule";
import { EmailTemplateEditor } from "./EmailTemplateEditor";
import { Pencil, MailOpen, RotateCw } from "lucide-react";
import { getVzDigestConfig, updateVzDigestConfig, getVzDigestDealers, previewVzDigest, sendVzTestDigest, getVzDigestLog, getVzFailedDigests, resendVzFailedDigests } from "@/lib/verizonSalesApi";

export const WeeklyDigestPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [dealerId, setDealerId] = useState("");
  const [preview, setPreview] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  const [failed, setFailed] = useState(null);
  const loadLog = useCallback(() => {
    getVzDigestLog().then(setLog).catch(() => {});
    getVzFailedDigests().then(setFailed).catch(() => {});
  }, []);
  const loadDealers = useCallback(() => getVzDigestDealers().then((d) => {
    setDealers(d); setDealerId((cur) => cur || d[0]?.id || "");
  }).catch(() => {}), []);
  useEffect(() => {
    getVzDigestConfig().then(setCfg).catch(() => toast.error("Failed to load digest settings"));
    loadDealers();
    loadLog();
  }, [loadLog, loadDealers]);
  if (cfg === null) return null;
  const enabled = cfg.enabled;
  const dealer = dealers.find((d) => d.id === dealerId);

  const toggle = async (v) => {
    try { setCfg(await updateVzDigestConfig({ enabled: v })); toast.success(v ? "Weekly digest turned ON" : "Weekly digest turned OFF"); }
    catch { toast.error("Save failed"); }
  };
  const doPreview = async () => {
    setBusy(true);
    try { setPreview(await previewVzDigest(dealerId)); } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); } finally { setBusy(false); }
  };
  const sendTest = async () => {
    setBusy(true);
    try { const r = await sendVzTestDigest(dealerId); toast.success(`Test digest sent to ${r.sent_to}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Send failed"); } finally { setBusy(false); }
  };

  const resendFailed = async () => {
    if (!window.confirm(`Resend the digest to ${failed.count} dealer(s) whose last digest failed? It will be rebuilt with today's numbers.`)) return;
    setBusy(true);
    try { const r = await resendVzFailedDigests(); toast.success(`Resending ${r.queued} digest(s). The list refreshes in about 30 seconds.`); setTimeout(loadLog, 30000); }
    catch (e) { toast.error(e.response?.data?.detail || "Resend failed"); } finally { setBusy(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="vz-digest-panel">
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4 text-teal" /> Weekly dealer digest</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-slate-500">On the days and time you pick below, each dealer gets the <b>Sales Performance, Retention &amp; Operational Report</b> in their own time zone: month-to-date DLAR numbers for all their stores (sales, prior month, retention + refill needed, inventory, plans &amp; ports, customer quality). MR targets come from the Store Goals quota bar.</p>
        <div className="flex items-center gap-3">
          <Switch checked={enabled} onCheckedChange={toggle} data-testid="vz-digest-enabled-switch" />
          <span className="text-sm font-medium">{enabled ? "Digest is ON" : "Digest is OFF"}</span>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Select value={dealerId} onValueChange={setDealerId}>
            <SelectTrigger className="h-9 w-72 text-xs" data-testid="vz-digest-dealer-select"><SelectValue placeholder="Choose dealer to preview" /></SelectTrigger>
            <SelectContent>{dealers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name || d.email} · {d.stores} {d.stores === 1 ? "store" : "stores"}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={doPreview} disabled={busy || !dealerId} data-testid="vz-digest-preview-btn"><Eye className="w-4 h-4 mr-1" /> Preview</Button>
          <Button onClick={sendTest} disabled={busy || !dealerId} data-testid="vz-digest-test-btn"><Send className="w-4 h-4 mr-1" /> Send test digest to me</Button>
          <Button variant="ghost" onClick={() => setEditing(true)} data-testid="vz-digest-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Edit email</Button>
          <EmailTemplateEditor kind="digest" open={editing} onOpenChange={setEditing} dealerId={dealerId} />
        </div>
        <DigestCcSettings cfg={cfg} onSaved={(c) => { setCfg(c); loadDealers(); }} />
        {dealer && <DealerCcEditor key={dealer.id + dealer.cc.join()} dealer={dealer} onSaved={loadDealers} />}
        {dealer && <DealerTimezone dealer={dealer} onSaved={loadDealers} />}
        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="vz-digest-preview">
            <div className="text-xs bg-slate-50 px-3 py-1.5 text-slate-600">{preview.subject} · to {preview.to.join(", ")}</div>
            <iframe title="digest preview" srcDoc={preview.html} className="w-full h-[720px] bg-white" />
          </div>
        )}
        <DigestSchedule key={cfg.schedule.days.join() + cfg.schedule.time} cfg={cfg} onSaved={setCfg} />
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-semibold text-slate-600">Recent digests</div>
            <div className="flex items-center gap-2">
              {failed?.count > 0 && <span className="text-xs text-red-600" data-testid="vz-digest-failed-count">{failed.count} dealer(s) missed their last digest</span>}
              <Button size="sm" variant="outline" onClick={resendFailed} disabled={busy || !failed?.count} data-testid="vz-digest-resend-failed-btn"><RotateCw className="w-3.5 h-3.5 mr-1" /> Resend failed</Button>
              <Button size="sm" variant="ghost" onClick={loadLog} data-testid="vz-digest-log-refresh">Refresh</Button>
            </div>
          </div>
          {log.length === 0 ? <p className="text-xs text-slate-500">No digests sent yet.</p> : (
            <div className="space-y-1 text-xs max-h-72 overflow-y-auto" data-testid="vz-digest-log">
              {log.map((l) => (
                <div key={l.id} className="flex justify-between border-b border-slate-100 py-1">
                  <span>{l.resent && <b className="text-teal">Resent · </b>}{l.dealer_email} · {l.week} · {l.stores} stores{l.cc?.length ? ` · cc ${l.cc.length}` : ""}{l.timezone ? ` · ${l.local_time} ${zoneName(l.timezone)}` : ""}</span>
                  <span className="flex items-center gap-3">
                    {l.status === "sent" && l.tracked && (l.opened_at
                      ? <span className="text-teal flex items-center gap-1" data-testid={`vz-digest-opened-${l.id}`}><MailOpen className="w-3.5 h-3.5" /> Dealer opened {new Date(l.opened_at).toLocaleString()}{l.open_count > 1 ? ` · ${l.open_count} opens` : ""}</span>
                      : <span className="text-slate-400" data-testid={`vz-digest-opened-${l.id}`}>Not opened yet</span>)}
                    <span className={l.status === "sent" ? "text-emerald-600" : "text-red-600"}>{l.status}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
